import React from 'react';

/**
 * GameScoreInfo component displays additional score information for a game
 * 
 * @param {Object} props - Component props
 * @param {Array} props.leaderboard - Array of player objects for the game
 * @param {Object} props.userEntry - User entry in the leaderboard
 * @param {boolean} props.isRanked - Whether the player is ranked in this game
 */
function GameScoreInfo({ leaderboard, userEntry, isRanked }) {
  // If no leaderboard or empty leaderboard, return null
  if (!leaderboard || leaderboard.length === 0) {
    return null;
  }

  // Find the highest score (first entry in the leaderboard)
  const highestEntry = leaderboard[0];
  
  // Find the score at rank 50 or the last entry if less than 50 entries
  const leaderboardLength = leaderboard.length;
  const rank50Entry = leaderboardLength >= 50 ? leaderboard[49] : leaderboard[leaderboardLength - 1];

  const isGrandChampion = isRanked && userEntry && userEntry.rank === 1;

  const nextRankEntry = (isRanked && userEntry && userEntry.rank > 1) 
    ? leaderboard.find(entry => entry.rank === userEntry.rank - 1) 
    : null;

  const scoreToBeatEntry = (isRanked && userEntry) ? nextRankEntry : rank50Entry;
  const showScoreToBeat = scoreToBeatEntry && scoreToBeatEntry.score !== highestEntry.score;

  const lastPlayer = leaderboardLength > 0 ? leaderboard[leaderboardLength - 1] : null;
  const isLastPlayerEntry = (entry) => {
    if (!entry || !lastPlayer) return false;
    return entry.rank === lastPlayer.rank || (rank50Entry && entry.rank === rank50Entry.rank);
  };

  return (
    <div className="score-info">
      <div className="top-score-section">
        {!isGrandChampion && (
          <p><strong>Top Score:</strong> {highestEntry.score?.toLocaleString()}</p>
        )}
        
        {isRanked && userEntry && userEntry.rank > 1 && (
          <p className="percentage-info behind">
            (+{((highestEntry.score - userEntry.score) / userEntry.score * 100).toFixed(1)}% needed)
          </p>
        )}
        {isGrandChampion && leaderboard.length > 1 && (
          <p className="percentage-info ahead">
            ({((userEntry.score - leaderboard[1].score) / leaderboard[1].score * 100).toFixed(1)}% ahead)
          </p>
        )}
      </div>

      {isRanked && userEntry ? (
        // Show next rank score if user is ranked and score to beat is different from top score
        showScoreToBeat && nextRankEntry && (
          <div className="next-rank-section">
            <p>
              <strong>Score to beat #{userEntry.rank - 1}:</strong>{' '}
              <span className={isLastPlayerEntry(nextRankEntry) ? 'large-score' : ''}>
                {nextRankEntry.score?.toLocaleString()}
              </span>
            </p>
            <p className="percentage-info behind">
              (+{((nextRankEntry.score - userEntry.score) / userEntry.score * 100).toFixed(1)}% needed)
            </p>
          </div>
        )
      ) : (
        // Show lowest ranked score if user is not ranked and score to beat is different from top score
        showScoreToBeat && rank50Entry && (
          <div className="rank50-section">
            <p>
              <strong>Score to beat #{rank50Entry.rank}:</strong>{' '}
              <span className={isLastPlayerEntry(rank50Entry) ? 'large-score' : ''}>
                {rank50Entry.score?.toLocaleString()}
              </span>
            </p>
          </div>
        )
      )}
    </div>
  );
}

export default GameScoreInfo;